/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Spinner from 'modules/common/spinner.component';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { showToast } from 'modules/layout/layout.actions';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { checkDirty } from 'modules/authorizations/utils';
import { formatMoney } from 'utilities/currencyFormatter';
import { EXPENSE_LINE_APPROVAL_STATUS, TRANSFEREE_PAYEE_TYPE, ZERO_GUID } from './expenseDetailsView.container';
import ReportLineItems from './reportLineItems.component';
import { POST_PUSH_EXPENSE_TO_ACUMATICA_FAILURE } from 'modules/expenses/store/types';
import { getCurrencyOptions } from 'modules/enumerations/enumerationHelpers';

const useStyles = makeStyles(theme => ({
  card: {
    width: '100%',
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  iconRow: { display: 'flex', flexDirection: 'row' },
  mb2: { marginBottom: '4px', marginRight: '16px' }
}));

const ReportDetailsCard = props => {
  const {
    isReadOnly = false,
    enumerations,
    expenseDetail,
    authDetails,
    onUpdateExpense,
    showToast,
    expenseCodes,
    onSaveLineItem,
    onDeleteLineItem,
    expenseId,
    onBulkUpdateStatus,
    onCopyLineItem,
    updateParentExpenseAttachments,
    updateParentExpenseLineAttachments,
    exceptionSummaries,
    authId,
    setExceptions,
    pushExpenseToAcumaticaFunc
  } = props;

  const classes = useStyles();

  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const currencies = enumerations?.currencies ?? [];
  const reimbursementTypes = enumerations?.reimbursementTypes ?? [];
 // const batchTypes = enumerations?.batchTypes ?? [];
  const expensePaidTos = enumerations?.expensePaidTos ?? [];
  const notPayables = enumerations?.notPayables ?? [];

  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  //const authAddresses = authDetails?.addresses ?? [];
  const authBanks = authDetails?.bankAccounts ?? [];
  const initialDraft = useMemo(() => {
    const e = expenseDetail?.expenseInformation ?? {};
    return {
      expenseInformation: {
        expenseId: e.expenseId ?? 0,
        payeeId: e.payeeId ?? 0,
        expenseStatusId: e.expenseStatusId ?? ZERO_GUID,
        dateSubmitted: e.dateSubmitted ?? null,
        description: e.description ?? '',
        total: e.total ?? 0,
        expenseAnalystId: e.expenseAnalystId ?? null,
        reimbursementTypeId: e.reimbursementTypeId ?? ZERO_GUID,
        payInCurrencyId: e.payInCurrencyId ?? ZERO_GUID,
        equivalentCurrencyId: e.equivalentCurrencyId ?? ZERO_GUID,
        batchTypeId: ZERO_GUID,
        expensePaidToId: e.expensePaidToId ?? ZERO_GUID,
        notPayableId: e.notPayableId ?? ZERO_GUID,
        isNotPayable: !!e.isNotPayable,
        isRushPayment: !!e.isRushPayment,
        isExpenseApproved: !!e.isExpenseApproved,
        internalNotes: e.internalNotes ?? '',
      },
      payee: {
        ...expenseDetail?.payee,
        payeeTypeId: expenseDetail?.payee?.payeeTypeId ?? TRANSFEREE_PAYEE_TYPE,
        authorizationId: expenseDetail?.payee?.authorizationId ?? null,
        transfereePayeeId: expenseDetail?.payee?.transfereePayeeId ?? null
      },
      addressInformation: expenseDetail?.addressInformation ?? null,
      bankingInformation: expenseDetail?.bankingInformation ?? null,
      expenseLineItems: expenseDetail?.expenseLineItems ?? []
    };
  }, [expenseDetail]);

  const [draft, setDraft] = useState(initialDraft);
  useEffect(() => setDraft(initialDraft), [initialDraft]);

  const { totalApproved, totalDenied, totalPending } = useMemo(() => {
    const items = draft?.expenseLineItems ?? expenseDetail?.expenseLineItems ?? [];
    const safe = n => (n == null || n === '' ? 0 : Number(n));
    const approved = items.reduce((a, i) => a + safe(i.approvedAmount ?? 0), 0);
    const total = items.reduce((a, i) => a + safe(i.totalAmount ?? 0), 0);
    const denied = Math.max(total - approved, 0);
    const pending = 0;
    return {
      totalApproved: approved,
      totalDenied: denied,
      totalPending: pending
    };
  }, [draft?.expenseLineItems, expenseDetail?.expenseLineItems]);

  useEffect(() => {
    if (expenseDetail) setIsDirty(checkDirty(expenseDetail, draft));
  }, [expenseDetail, draft]);

  const isCompleted = !!draft?.expenseInformation?.isExpenseApproved;

  const hasPendingOrDraft = (draft?.expenseLineItems ?? []).some(
    li =>
      EXPENSE_LINE_APPROVAL_STATUS.PENDING &&
      (String(li.lineItemStatusId).toLowerCase() === String(EXPENSE_LINE_APPROVAL_STATUS.PENDING).toLowerCase() ||
        li.lineItemStatusId === ZERO_GUID)
  );

  const isCompletable = (draft?.expenseLineItems?.length ?? 0) > 0 && !hasPendingOrDraft && !isCompleted;
  const isAcumaticaRejected = expenseDetail?.expenseInformation?.acumaticaProcessStatus === 'R';
  const readOnlyEffective = isReadOnly || isCompleted;

  const toExpenseSavePayload = d => ({
    ...d,
    bankingInformation: d.bankingInformation
      ? {
          expenseBankingId: d.bankingInformation.expenseBankingId ?? null,
          expenseId: d.expenseInformation?.expenseId ?? null,
          bankId: d.bankingInformation.bankId ?? null
        }
      : null
  });

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await onUpdateExpense(toExpenseSavePayload(draft));
      showToast('Report saved.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
    } catch (e) {
      showToast('Failed to save the report.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setIsLoading(true);
      const completed = {
        ...draft,
        expenseInformation: { ...draft.expenseInformation, isExpenseApproved: true }
      };
      await onUpdateExpense(toExpenseSavePayload(completed));
      showToast('Report completed.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
    } catch (e) {
      showToast('Failed to complete the report.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } finally {
      setIsLoading(false);
    }
  };

  const resetExpenseStatus = async () => {
    setIsLoading(true);
    const reset = {
      ...draft,
      expenseInformation: { ...draft.expenseInformation, isExpenseApproved: false }
    };
    await onUpdateExpense(toExpenseSavePayload(reset), true);
    setIsLoading(false);
  };

  const pushToAcumatica = async () => {
    setIsLoading(true);
    const resp = await pushExpenseToAcumaticaFunc();
    setIsLoading(false);
    if (resp?.type === POST_PUSH_EXPENSE_TO_ACUMATICA_FAILURE) {
      props.showToast('Failed to push the expense to Acumatica, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return false;
    }
    return true;
  };

  const currencyOpts = useMemo(() => getCurrencyOptions(currencies), [currencies]);

  const reimbursementOpts = useMemo(() => reimbursementTypes.filter(c => c.isActive), [reimbursementTypes]);
  //const batchTypeOpts = useMemo(() => batchTypes.filter(c => c.isActive), [batchTypes]);
  const expensePaidToOpts = useMemo(() => expensePaidTos.filter(x => x.isActive), [expensePaidTos]);
  const notPayableOpts = useMemo(() => notPayables.filter(x => x.isActive), [notPayables]);

  // const addressOpts = authAddresses.map(a => ({
  //   id: a.addressId,
  //   title: `${a.addressLineOne} ${a.city}, ${a.provinceId} ${a.zipcode}`.trim()
  // }));

  const bankOpts = authBanks.map(b => ({
    id: b.bankId,
    title: b.bankNickName
  }));

  const setEI = patch => setDraft(d => ({ ...d, expenseInformation: { ...d.expenseInformation, ...patch } }));

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card} sx={{ marginTop: '10px' }}>
        <Box>
          <CardContent className={classes.cardContainer}>
            <div className="row justify-space-between align-center">
              <div className={classes.iconRow}>
                <FontAwesomeIcon icon={['fas', 'dollar-sign']} size="2x" />
                <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                  Report Details
                </Typography>
              </div>
            </div>
          </CardContent>
          <CardContent sx={{ padding: '10px', marginLeft: '10px', paddingTop: '0px' }}>
            <Grid container spacing={1}>
              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Report Description
                    </Typography>
                  </div>
                  <TextField
                    key={'reportDescription'}
                    name={'reportDescription'}
                    disabled={readOnlyEffective}
                    value={draft.expenseInformation.description}
                    sx={{ maxWidth: '500px' }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={e => setEI({ description: e.target.value })}
                  />
                </Box>
              </Grid>
              <Grid item md={3}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Bank Account
                    </Typography>
                  </div>
                  <Select
                    labelId={'bankAccount'}
                    key={'bankAccount'}
                    name={'bankAccount'}
                    value={draft.bankingInformation?.bankId ?? ''}
                    sx={{ maxWidth: '360px', minWidth: '260px' }}
                    onChange={e => {
                      const id = e.target.value;
                      const selected = authBanks.find(b => b.bankId === id) ?? null;
                      setDraft(d => ({ ...d, bankingInformation: selected }));
                    }}
                    disabled={readOnlyEffective || bankOpts.length === 0}
                  >
                    {bankOpts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>

              <Grid item md={2}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Expense Paid To
                    </Typography>
                  </div>
                  <Select
                    labelId={'expensePaidTo'}
                    key={'expensePaidTo'}
                    name={'expensePaidTo'}
                    value={draft.expenseInformation.expensePaidToId}
                    sx={{ maxWidth: '360px', minWidth: '180px' }}
                    disabled={readOnlyEffective}
                    onChange={e => setEI({ expensePaidToId: e.target.value })}
                  >
                    {expensePaidToOpts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
              <Grid item md={2}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Reimbursement Type
                    </Typography>
                  </div>
                  <Select
                    labelId={'reimbursementType'}
                    key={'reimbursementType'}
                    name={'reimbursementType'}
                    value={draft.expenseInformation.reimbursementTypeId}
                    sx={{ maxWidth: '360px', minWidth: '180px' }}
                    disabled={readOnlyEffective}
                    onChange={e => setEI({ reimbursementTypeId: e.target.value })}
                  >
                    {reimbursementOpts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
              <Grid item md={2}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Pay in Currency Code
                    </Typography>
                  </div>
                  <Select
                    labelId={'payInCurrency'}
                    key={'payInCurrency'}
                    name={'payInCurrency'}
                    value={draft.expenseInformation.payInCurrencyId}
                    sx={{ maxWidth: '360px', minWidth: '180px' }}
                    disabled={readOnlyEffective}
                    onChange={e => setEI({ payInCurrencyId: e.target.value })}
                  >
                    {currencyOpts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                        {opt.title} ({opt.code})
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
              <Grid item md={1}>
                <Box sx={{ paddingTop: '8px' }}>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Total Approved
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {formatMoney(totalApproved)}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={1}>
                <Box sx={{ paddingTop: '8px' }}>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Total Denied
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {formatMoney(totalDenied)}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={1}>
                <Box sx={{ paddingTop: '8px' }}>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Total Pending
                    </Typography>
                  </div>
                  <div className={classes.mb2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, fontSize: '1rem' }}>
                      {formatMoney(totalPending)}
                    </Typography>
                  </div>
                </Box>
              </Grid>
              <Grid item md={2}>
                <Box sx={{ paddingTop: '8px' }}>
                  <FormControl>
                    <FormControlLabel
                      key={'rushPayment'}
                      name={'rushPayment'}
                      label={'Rush Payment'}
                      labelPlacement="end"
                      control={<Checkbox disabled={readOnlyEffective} checked={!!draft.expenseInformation.isRushPayment} />}
                      sx={{ minWidth: '180px', maxWidth: '360px', marginLeft: '0px', marginRight: '8px' }}
                      disabled={readOnlyEffective}
                      onChange={(_, checked) => setEI({ isRushPayment: checked })}
                    />
                  </FormControl>
                </Box>
              </Grid>
              <Grid item md={1}></Grid>
              {/* <Grid item md={2}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Batch Type
                    </Typography>
                  </div>
                  <Select
                    labelId={'batchType'}
                    key={'batchType'}
                    name={'batchType'}
                    value={draft.expenseInformation.batchTypeId}
                    sx={{ maxWidth: '360px', minWidth: '180px' }}
                    disabled={readOnlyEffective}
                    onChange={e => setEI({ batchTypeId: e.target.value })}
                  >
                    {batchTypeOpts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid> */}
              <Grid item md={2}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Not Payable
                    </Typography>
                  </div>
                  <Select
                    labelId={'notPayable'}
                    key={'notPayable'}
                    name={'notPayable'}
                    value={draft.expenseInformation.notPayableId}
                    sx={{ maxWidth: '360px', minWidth: '180px' }}
                    disabled={readOnlyEffective}
                    onChange={e =>
                      setDraft(d => ({
                        ...d,
                        expenseInformation: {
                          ...d.expenseInformation,
                          notPayableId: e.target.value
                        }
                      }))
                    }
                  >
                    {notPayableOpts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
              <Grid item md={2}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Equivalent Currency Code
                    </Typography>
                  </div>
                  <Select
                    labelId={'equivalentCurrency'}
                    key={'equivalentCurrency'}
                    name={'equivalentCurrency'}
                    value={draft.expenseInformation.equivalentCurrencyId}
                    sx={{ maxWidth: '360px', minWidth: '180px' }}
                    disabled={readOnlyEffective}
                    onChange={e => setEI({ equivalentCurrencyId: e.target.value })}
                  >
                    {currencyOpts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }}>
                        {opt.title} ({opt.code})
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>
              <Grid item md={6}>
                <Box>
                  <div className={classes.mb2}>
                    <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400, fontSize: '1rem' }}>
                      Internal Notes
                    </Typography>
                  </div>
                  <TextField
                    key={'internalNotes'}
                    name={'internalNotes'}
                    disabled={readOnlyEffective}
                    value={draft.expenseInformation.internalNotes}
                    sx={{ maxWidth: '1000px' }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={e => setEI({ internalNotes: e.target.value })}
                  />
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ marginTop: '16px' }}>
              <ReportLineItems
                items={draft?.expenseLineItems ?? []}
                enumerations={enumerations}
                expenseCodes={expenseCodes}
                isReadOnly={readOnlyEffective}
                onSaveLineItem={onSaveLineItem}
                onDeleteLineItem={onDeleteLineItem}
                expenseId={expenseId}
                onBulkUpdateStatus={onBulkUpdateStatus}
                onCopyLineItem={onCopyLineItem}
                expenseDetail={expenseDetail}
                updateParentExpenseAttachments={updateParentExpenseAttachments}
                updateParentExpenseLineAttachments={updateParentExpenseLineAttachments}
                exceptionSummaries={exceptionSummaries}
                selectedExpenseExceptions={draft?.expenseLineItemExceptions ?? []}
                authId={authId}
                setExceptions={setExceptions}
                pushToAcumatica={pushToAcumatica}
                isAcumaticaRejected={isAcumaticaRejected}
              />
            </Box>
            <Box sx={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isLoading && <Spinner />}
                  {isAcumaticaRejected && !isLoading && isCompleted && (
                    <Button color="primary" variant="contained" sx={{ maxHeight: '40px' }} onClick={pushToAcumatica} disabled={!isDirty}>
                      Push to Acumatica
                    </Button>
                  )}
                  {!readOnlyEffective && !isLoading ? (
                    <>
                      <Button color="primary" variant="contained" sx={{ maxHeight: '40px' }} onClick={handleSave} disabled={!isDirty}>
                        Save Report
                      </Button>
                      <Button
                        color="secondary"
                        variant="contained"
                        sx={{ maxHeight: '40px' }}
                        onClick={handleComplete}
                        disabled={!isCompletable || isLoading}
                      >
                        Complete Report
                      </Button>
                    </>
                  ) : (
                    <>
                      {!isLoading && (
                        <Button color="primary" variant="contained" sx={{ maxHeight: '40px' }} onClick={resetExpenseStatus}>
                          Reset Expense Status
                        </Button>
                      )}
                    </>
                  )}
                </Box>
                {!isLoading && !readOnlyEffective && (
                  <>
                    {!isCompletable && !isCompleted && (
                      <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'right' }}>
                        All line items must be approved or denied before completing.
                      </Typography>
                    )}
                    {isCompleted && (
                      <Typography variant="caption" sx={{ mt: 1, color: 'success.main', textAlign: 'right' }}>
                        This report has been completed and is now read-only.
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </CardContent>
        </Box>
      </Card>
    </ThemeProvider>
  );
};

export default compose(
  connect(null, {
    showToast
  })
)(ReportDetailsCard);
